import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import PageLayout from "../components/PageLayout";
import CtaBand from "../components/CtaBand";
import ActivityPartner from "../components/ActivityPartner";
import { Eyebrow, Reveal } from "../components/Reveal";
import { pages } from "./data/pageData";
import { useContent } from "../content-context";
import ActivityBookingSection from "../components/ActivityBookingSection";

export default function LandingPage({ pageKey }) {
  const page = pages[pageKey];
  const { weddingsVideo, henVideo } = useContent();
  const pageVideos = { weddings: weddingsVideo, hen: henVideo };
  const featuredVideo = pageVideos[pageKey] || null;
  return (
    <PageLayout seo={page.seo}>
      {page.heroBg ? (
        <section className="relative flex min-h-[88vh] items-end overflow-hidden" data-testid="page-hero-fullbleed">
          <div
            data-testid="page-hero-bg"
            role="img"
            aria-label={page.heroImageAlt}
            className="absolute inset-0 bg-[length:400%_auto] bg-[position:center_85%] bg-no-repeat md:bg-cover"
            style={{ backgroundImage: `url(${page.heroBg})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/50 to-navy/10" aria-hidden="true" />
          <div className="relative mx-auto w-full max-w-[1440px] px-5 pb-16 pt-44 md:px-10 md:pb-24">
            <Reveal>
              <Eyebrow>{page.eyebrow}</Eyebrow>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="mt-6 max-w-3xl font-display text-4xl font-medium leading-[1.05] text-ivory md:text-6xl" data-testid="page-h1">
                {page.h1}
              </h1>
            </Reveal>
            {page.heroParas.map((p, i) => (
              <Reveal key={i} delay={0.14 + i * 0.06}>
                <p className="mt-5 max-w-xl text-sm font-light leading-relaxed text-ivory/80 md:text-base">{p}</p>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <Link
                to={page.cta.to || "/contact/"}
                data-testid="page-hero-cta"
                className="mt-9 inline-flex items-center gap-3 bg-gold px-9 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-navy transition-colors duration-500 hover:bg-ivory"
              >
                {page.cta.label}
              </Link>
            </Reveal>
          </div>
        </section>
      ) : (
        <section className="relative overflow-hidden pt-28 md:pt-36">
          <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-12 px-5 pb-16 md:px-10 lg:grid-cols-2 lg:gap-16">
            <div>
              <Reveal>
                <Eyebrow>{page.eyebrow}</Eyebrow>
              </Reveal>
              <Reveal delay={0.08}>
                <h1 className="mt-6 font-display text-4xl font-medium leading-[1.05] text-navy md:text-6xl" data-testid="page-h1">
                  {page.h1}
                </h1>
              </Reveal>
              {page.heroParas.map((p, i) => (
                <Reveal key={i} delay={0.14 + i * 0.06}>
                  <p className="mt-5 max-w-xl text-sm font-light leading-relaxed text-navy/75 md:text-base">{p}</p>
                </Reveal>
              ))}
              <Reveal delay={0.3}>
                <Link
                  to={page.cta.to || "/contact/"}
                  data-testid="page-hero-cta"
                  className="mt-9 inline-flex items-center gap-3 bg-navy px-9 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-ivory transition-colors duration-500 hover:bg-gold hover:text-navy"
                >
                  {page.cta.label}
                </Link>
              </Reveal>
            </div>
            <Reveal delay={0.15}>
              <div className="aspect-[4/3] overflow-hidden" data-testid="page-hero-image">
                <img src={page.heroImage} alt={page.heroImageAlt} className="h-full w-full object-cover" />
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {featuredVideo && featuredVideo.src && (
        <section className="bg-navy py-16 md:py-24" data-testid="weddings-featured-video">
          <div className="mx-auto max-w-[1100px] px-5 md:px-10">
            <Reveal>
              <p className="text-center text-[11px] font-semibold uppercase tracking-[0.28em] text-gold">
                {featuredVideo.eyebrow || "A Real Wedding on the Island"}
              </p>
              <h2 className="mt-5 text-center font-display text-3xl font-medium text-ivory md:text-5xl">
                {featuredVideo.heading || "Watch a Collanmore Wedding"}
              </h2>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="mt-10 border border-ivory/15 bg-navy-2 p-3 md:p-4">
                <div className="flex justify-center">
                  <video
                    src={featuredVideo.src}
                    controls
                    playsInline
                    preload="metadata"
                    poster={featuredVideo.poster || undefined}
                    className="mx-auto block max-h-[78vh] w-auto max-w-full bg-black"
                    data-testid="weddings-video-player"
                  />
                </div>
                {featuredVideo.caption && (
                  <p className="px-2 pb-2 pt-4 text-center text-sm font-light text-ivory/70">{featuredVideo.caption}</p>
                )}
              </div>
            </Reveal>
            <script
              type="application/ld+json"
              data-testid="weddings-video-schema"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "VideoObject",
                  name: featuredVideo.heading || "A Real Wedding on Collanmore Island",
                  description:
                    featuredVideo.caption ||
                    "A glimpse of a real wedding day on Collanmore Island — an exclusive-use private island lodge in Clew Bay, County Mayo, Ireland.",
                  thumbnailUrl: (typeof window !== "undefined" ? window.location.origin : "") + (featuredVideo.poster || ""),
                  uploadDate: featuredVideo.uploadDate || "2026-10-31",
                  contentUrl: (typeof window !== "undefined" ? window.location.origin : "") + featuredVideo.src,
                  publisher: {
                    "@type": "Organization",
                    name: "Collanmore Island Lodge",
                    url: "https://www.collanmoreislandlodge.com",
                  },
                }),
              }}
            />
          </div>
        </section>
      )}

      {page.sections.map((sec, i) => (
        <section key={i} className={`py-16 md:py-20 ${i % 2 === 1 ? "bg-white" : ""}`}>
          <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-10 px-5 md:px-10 lg:grid-cols-2 lg:gap-16">
            <Reveal className={i % 2 === 1 ? "lg:order-2" : ""}>
              {sec.image && (
                <div className="aspect-[4/3] overflow-hidden" data-testid={`page-section-image-${i + 1}`}>
                  <img src={sec.image} alt={sec.imageAlt || sec.h2} loading="lazy" className="h-full w-full object-cover" />
                </div>
              )}
              {sec.list && (
                <ul className="flex flex-col gap-3 border border-navy/10 bg-white p-8 shadow-[0_8px_32px_rgba(10,25,47,0.06)]" data-testid={`page-section-list-${i + 1}`}>
                  {sec.list.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm font-light text-navy/85 md:text-base">
                      <Check className="mt-1 h-4 w-4 shrink-0 text-gold" /> {item}
                    </li>
                  ))}
                </ul>
              )}
            </Reveal>
            <div className={i % 2 === 1 ? "lg:order-1" : ""}>
              <Reveal delay={0.1}>
                <h2 className="font-display text-3xl font-medium leading-tight text-navy md:text-5xl" data-testid={`page-h2-${i + 1}`}>
                  {sec.h2}
                </h2>
              </Reveal>
              {sec.paras &&
                sec.paras.map((p, j) => (
                  <Reveal key={j} delay={0.16 + j * 0.05}>
                    <p className="mt-5 max-w-xl text-sm font-light leading-relaxed text-navy/75 md:text-base">{p}</p>
                  </Reveal>
                ))}
            </div>
          </div>
        </section>
      ))}

      {page.partner && <ActivityPartner />}
      {(pageKey === "adventure" || pageKey === "sauna") && <ActivityBookingSection />}

      <CtaBand
        heading={page.finalHeading || "Start Planning Your Island Escape"}
        lead={page.finalLead}
        cta={page.cta.label}
        to={page.cta.to || "/contact/"}
      />
    </PageLayout>
  );
}
